
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { AppFacadeService } from './app-facade.service';
import { map, take, withLatestFrom } from 'rxjs/operators';
import { RouterFacadeService } from './router-facade.service';
import { Role } from '@enums/role.enum';
import { Location } from "@angular/common";
import {BusinessFacadeService} from './business/business-facade.service';

/**
 * AuthGuard prevents the user to log in
 * again if a current session exists, leading
 * the user to the default home page depending
 * on the role. If there isn't a current session
 * then the user is leaded to the authentication
 * page
 */
@Injectable({
    providedIn: 'root'
})
export class AuthGuard implements CanActivate {

    /**
     * AuthGuard Constructor
     * @param appFacadeService The app facade service to get the logged user information
     * @param routerFacadeService The router service to lead the user to the home page depending on the role
     */
    constructor(
        private appFacadeService: AppFacadeService,
        private routerFacadeService: RouterFacadeService,
        private businessFacadeService: BusinessFacadeService,
    ) { }

    /**
     * Defines if the user should be leaded to the authentication
     * page or to the home page. When it returns true the user will be leaded
     * to the authentication page. When it returns false the user will be
     * leaded to the following pages depending on the role:
     * - Site Admin: Admin Reseller page ({@link AdminResellerComponent})
     * - Reseller Admin: Reseller dashboard page ({@link ResellerDashboardComponent})
     * - Business Admin: Business dashboard page ({@link BusinessDashboardComponent})
     * - Business User: Location dahboard page ({@link LocationDashboardComponent})
     * @param _next 
     * @param _state 
     * @returns An observable of a boolean that determines if the 
     * user should be leaded to the authentication page or not.
     */
    canActivate(next: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
        return this.appFacadeService.getRoleLoggedUser$().pipe(
            withLatestFrom(this.businessFacadeService.getSelectedBusiness$()),
            take(1),
            map(([role, selectedBusiness]) => {
                // the user already has a selected business from a previous session
                if (role != Role.None && Object.values(Role).includes(role) && selectedBusiness?.id != null) {
                    this.routerFacadeService.gotoBusinessDashboard$();
                    return false;
                }

                // the user has not selected a business yet; default heuristic
                switch (role) {
                    case Role.SiteAdmin:
                        this.routerFacadeService.gotoResellers$();
                        //this.routerFacadeService.gotoResellerSelection$();
                        break;
                    case Role.Reseller:
                        this.routerFacadeService.navigateTo$('/reseller/business');
                        break;
                    case Role.BusinessAdmin:
                    case Role.UserBusiness:
                        this.routerFacadeService.gotoBusinessDashboard$();
                        break;
                    default:
                        // user role not found
                        return true;
                }
            })
        )
    }
}
